import { ApiError } from "../api.js";

const SAFE_REJECTION_CODES = new Set([
  "booking_not_owned_by_student",
  "booking_preflight_failed",
  "credit_coupon_unavailable",
  "credit_being_set_up",
  "credit_claim_pending",
  "credit_depleted",
  "credit_migration_hold",
  "credit_not_available",
  "credit_unavailable",
  "forbidden",
  "idempotency_key_reused",
  "policy_action_not_allowed",
  "slot_unavailable",
  "staff_booking_prewrite_rejected",
  "existing_booking_payment",
]);

const BOOKING_STAGE_LABELS: Record<string, string> = {
  booking_preflight: "preparing the booking",
  final_slot_validation: "rechecking the selected time",
  staff_schedule_override_appointment: "creating the staff schedule-override appointment",
  staff_schedule_override_declined:
    "the booking system refused this time for a reason the schedule-policy override does not cover",
  staff_schedule_override_minimum_notice:
    "this lesson type has a minimum booking notice that the schedule-policy override could not satisfy",
  staff_schedule_override_preparation: "preparing the staff schedule-policy mutation",
  amelia_appointment_creation: "creating the Amelia appointment",
  amelia_response_validation: "confirming the Amelia appointment",
  lesson_credit_consumption: "recording the lesson credit",
  pending_payment_creation: "creating the pending payment",
  pending_payment_response_validation: "confirming the pending payment",
  portal_refresh_enqueue: "refreshing the Hub diary",
};

export type BookingFailureKind = "rejected" | "uncertain";

/**
 * Whether a failed HTTP exchange proves that the booking mutation did not
 * happen. A transport failure, an upstream failure, a reconciliation hold or
 * an unstructured conflict is uncertain and must retain the attempt key.
 *
 * `booking_preflight` is the one 5xx exception: WMF-SIte emits it only when no
 * durable booking operation exists for the idempotency key, so no lesson
 * mutation was started and a fresh attempt is safe.
 */
export function bookingFailureKind(cause: unknown): BookingFailureKind {
  if (!(cause instanceof ApiError)) return "uncertain";
  if (cause.code && SAFE_REJECTION_CODES.has(cause.code)) return "rejected";
  const body = cause.body;
  if (
    body !== null &&
    typeof body === "object" &&
    (body as { bookingStage?: unknown }).bookingStage === "booking_preflight"
  ) {
    return "rejected";
  }
  if (cause.status === 0 || cause.status >= 500) return "uncertain";
  if (cause.status === 409) return "uncertain";
  return "rejected";
}

export interface BookingAttemptAfterFailure {
  key: string;
  kind: BookingFailureKind;
}

/** The key belongs to the logical booking attempt, not to one HTTP request. */
export function bookingAttemptAfterFailure(
  currentKey: string,
  cause: unknown,
  createKey: () => string,
): BookingAttemptAfterFailure {
  const kind = bookingFailureKind(cause);
  return {
    key: kind === "uncertain" ? currentKey : createKey(),
    kind,
  };
}

export const uncertainBookingMessage =
  "The booking outcome is uncertain. Reload lessons and credits, then ask the office to reconcile it if the result is still unclear. Do not submit another booking or payment while this attempt is unresolved.";

/**
 * Keep the conservative uncertainty warning without throwing away the small
 * amount of safe diagnostic evidence supplied by the Hub.
 *
 * A 409 can be either a reconciliation hold or a definite pre-write conflict;
 * until the staff API gives those cases stable codes it must remain uncertain.
 * For a 5xx, the Hub exposes only an allow-listed booking stage and the request
 * reference. Raw Amelia/FastAPI detail never reaches this layer.
 */
export function uncertainBookingMessageFor(
  cause: unknown,
  baseMessage: string = uncertainBookingMessage,
): string {
  if (!(cause instanceof ApiError)) return baseMessage;

  if (cause.status === 409) {
    const response = cause.message.trim();
    if (response !== "") {
      return `${baseMessage} Booking system response: ${response}`;
    }
  }

  const body = cause.body;
  if (body === null || typeof body !== "object") return baseMessage;

  const diagnostic = body as {
    bookingStage?: unknown;
    requestId?: unknown;
    bookingDiagnostics?: unknown;
  };
  const evidence: string[] = [];
  if (typeof diagnostic.bookingStage === "string") {
    const label = BOOKING_STAGE_LABELS[diagnostic.bookingStage];
    if (label) evidence.push(`Failure stage: ${label}.`);
  }
  if (typeof diagnostic.requestId === "string" && diagnostic.requestId.trim() !== "") {
    evidence.push(`Reference: ${diagnostic.requestId.trim()}.`);
  }

  if (
    diagnostic.bookingDiagnostics !== null &&
    typeof diagnostic.bookingDiagnostics === "object"
  ) {
    const details = diagnostic.bookingDiagnostics as Record<string, unknown>;
    if (typeof details.version === "string" && /^\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(details.version)) {
      evidence.push(`WordPress companion version: ${details.version}.`);
    }
    const reasonLabels: Record<string, string> = {
      purpose_credential_unaccepted: "WordPress did not accept the staff override credential",
      admin_user_unconfigured: "the configured WordPress administrator is unavailable",
      admin_identity_not_returned: "WordPress did not supply the administrator identity",
      admin_identity_not_observed: "WordPress did not observe its administrator identity hook",
      settings_unavailable: "Amelia settings could not be read for this request",
      collision_guard_enabled: "the stored appointment collision guard prevented the override",
      settings_encoding_failed: "the request-scoped Amelia settings could not be encoded",
    };
    if (typeof details.reason === "string" && reasonLabels[details.reason]) {
      evidence.push(`Override setup: ${reasonLabels[details.reason]}.`);
    }
    if (details.reason_derived === "true") {
      evidence.push("WMF derived this diagnosis from the companion checkpoints.");
    }
    if (details.header === "missing") {
      evidence.push("WordPress override diagnostics were missing from the response.");
    }
  }

  return evidence.length > 0
    ? `${baseMessage} ${evidence.join(" ")}`
    : baseMessage;
}
